<template>
  <b-sidebar :id="sidebarFields" sidebar-class="custom-sidebar-container sidebar-fields" ref="sidebar_fields" title="Настройка колонок" right shadow backdrop>
    <div class="d-flex flex-column h-100">
      <div class="flex-grow-1 overflow-auto p-3">
        <draggable v-model="localFields" handle=".drag-handle">
          <div v-for="field in localFields" :key="field.id">
            <div v-if="field.isSeparator" class="font-weight-bold text-small text-muted text-uppercase p-2 my-1">
              {{ field.label }}
            </div>

            <div v-else class="export-list-item">
              <div class="d-flex align-items-center text-truncate">
                <span class="drag-handle text-dark mr-3">
                  <v-icon name="buttons/dots" :size="12"/>
                </span>
                <span class="text-small">{{ field.label }}</span>
                <span v-if="configureRequired && field.required && field.visible" class="text-danger font-weight-bold ml-2">*</span>
              </div>

              <div class="d-flex align-items-center" style="gap: 12px;">
                <b-form-checkbox
                  v-if="configureRequired"
                  v-model="field.required"
                  :disabled="!field.visible"
                  class="m-0"
                  title="Обязательное поле"
                />

                <div class="ant-switch-wrapper">
                  <b-form-checkbox
                    :disabled="visibleFields.length === 1 && field.visible"
                    v-model="field.visible"
                    switch
                    class="m-0"
                  />
                  <div class="switch-icons-overlay">
                    <v-icon v-show="field.visible" name="buttons/check" :size="11" class="ml-2" style="color: #fff" />
                    <span v-show="!field.visible" class="flex-grow-1"></span>
                    <v-icon v-show="!field.visible" name="buttons/cross" :size="10" class="mr-2" style="color: #fff" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </draggable>
      </div>

      <div class="export-sidebar-footer px-3 py-3 border-top">
        <div class="d-flex flex-wrap align-items-center" style="gap: 12px;">
          <b-button variant="link" class="text-small p-0" style="color: #F5222D !important; text-decoration: none !important;" @click="resetColumns">
            Сбросить
          </b-button>
          <b-button v-if="checkAll" class="text-small text-dark p-0" variant="link" style="text-decoration: none !important; color: #6c757d;" @click="checkAllColumns(false)">
            <v-icon name="buttons/toggle_left" :size="15" class="mr-1 pt-1"/> Снять все
          </b-button>
          <b-button v-else variant="link" class="text-small text-dark p-0" style="text-decoration: none !important" @click="checkAllColumns(true)">
            <v-icon name="buttons/toggle_right" :size="15" class="mr-1 pt-1"/> Выбрать все
          </b-button>
          <b-button class="custom-btn-primary ml-auto" @click="saveColumns(false)">
            <v-icon name="buttons/save" :size="13" class="mr-1" /> Сохранить
          </b-button>
        </div>
      </div>

    </div>
  </b-sidebar>
</template>

<script>
import draggable from "vuedraggable";

export default {
  name: 'DynamicTables',
  components: { draggable },
  props: {
    storageKey: { type: String, required: true },
    sidebarFields: { type: String, required: true },
    fieldsModel: { type: Array, required: true },
    fieldKey: { type: String, required: true },
    configureRequired: { type: Boolean, default: false }
  },
  data() {
    return {
      localFields: [],
      checkAll: true,
    };
  },
  async created() {
    this.initColumns();
  },
  computed: {
    visibleFields() {
      return this.localFields.filter(field => field.visible && !field.isSeparator);
    },
  },
  watch: {
    fieldsModel: {
      handler(newFields, oldFields) {
        const hasNewKeys = newFields.some(f => !oldFields.some(of => of.key === f.key));

        if (newFields.length !== oldFields.length || hasNewKeys) {
          this.initColumns();
        }
      },
      deep: true
    },
    localFields(newFields) {
      newFields.forEach(field => {
        if (this.configureRequired && !field.visible) {
          field.required = false;
        }
      });
      this.$emit('update:fieldsModel', newFields);
    }, deep: true
  },
  methods: {
    checkAllColumns(flag) {
      this.localFields.forEach(item => {
        if (item.isSeparator) return;

        item.visible = flag || item.key === this.fieldKey;
      })
      this.checkAll = flag;
    },
    getStoredColumns() {
      const storedFields = localStorage.getItem(this.storageKey);
      return storedFields ? JSON.parse(storedFields) : null;
    },
    mergeColumns(incomingFields) {
      const storedSettings = this.getStoredColumns();

      if (!storedSettings) {
        return [...incomingFields];
      }

      const mergedFields = storedSettings.map(storedField => {
        const baseField = incomingFields.find(f => f.key === storedField.key);

        if (baseField) {
          const storedRequired = storedField.required !== undefined && storedField.required !== null;
          const baseRequired = baseField.required !== undefined && baseField.required !== null;

          return {
            ...baseField,
            visible: storedField.visible,
            required: storedRequired ? storedField.required : (baseRequired ? baseField.required : false)
          };
        }
        return null;
      }).filter(Boolean);

      const storedKeys = new Set(mergedFields.map(f => f.key));
      const newFields = incomingFields
        .filter(f => !storedKeys.has(f.key))
        .map(f => ({
          ...f,
          required: f.required || false
        }));

      return [...mergedFields, ...newFields];
    },
    initColumns() {
      this.localFields = this.mergeColumns(this.fieldsModel);
      this.checkAll = this.localFields.every(field => field.visible);
    },
    saveColumns() {
      localStorage.setItem(this.storageKey, JSON.stringify(this.localFields));
      this.$bvToast.toast("Успешное сохранение колонок.", {
        title: 'Сохранено',
        variant: 'success',
        autoHideDelay: 3000,
        solid: true
      });
      this.$refs.sidebar_fields.hide();
    },
    async resetColumns() {
      this.$refs.sidebar_fields.hide();
      const isConfirmed = await this.$bvModal.msgBoxConfirm('Вы хотите вернуть стандартные настройки? (Это также обновит текущую страницу).', {
        title: 'Сброс настроек',
        size: 'md',
        buttonSize: 'sm',
        headerBgVariant: 'danger',
        okVariant: 'danger',
        okTitle: 'Сбросить',
        cancelTitle: 'Отмена',
        footerClass: 'p-1',
        centered: true
      });

      if (!isConfirmed) return;

      localStorage.removeItem(this.storageKey);
      this.initColumns();

      this.$bvToast.toast("Настройки таблицы сброшены.", {
        title: 'Успешно',
        variant: 'warning',
        autoHideDelay: 2000,
        solid: true
      });
      setTimeout(() => {
        location.reload();
      }, 1111);
    }
  },
}
</script>

<style>
.drag-handle {
  cursor: grab;
}
.sidebar-fields {
  z-index: 1060 !important;
}
</style>



// WEBPACK FOOTER //
// src/utilites/commonUi/DynamicTables.vue